/**
 * Injectable API runtime.
 *
 * `apiSdk.ts` is shared between the advisor app (`apps/app`) and the vault app
 * (`apps/vault`), but the two apps authenticate differently — the advisor app
 * attaches a primary-project Firebase token + `X-Org-Id`, while the vault app
 * attaches a secondary-project token and (once unlocked) a vault session token.
 * Rather than hard-code one app's auth wiring into the shared SDK, each app
 * constructs its own client + header builder and injects them here at startup
 * via `configureApi()`.
 */
import type { Client } from "./client/client";

export const API_BASE = import.meta.env.VITE_API_URL || "http://localhost:8000";

type AuthHeaderBuilder = (
  extra?: Record<string, string>
) => Promise<Record<string, string>>;

let _client: Client | null = null;
let _getAuthHeaders: AuthHeaderBuilder = async (extra) => ({
  ...(extra || {}),
});
let _getVaultSessionToken: () => string = () => "";

export interface ApiRuntimeConfig {
  /** Pre-configured client instance (base URL + interceptors). */
  client: Client;
  /** Builds the base auth headers (Firebase token, org id, etc.). */
  getAuthHeaders: AuthHeaderBuilder;
  /**
   * Returns the current vault session token, if any. Only the vault app sets
   * this; the advisor app leaves it defaulted to "" (its vault endpoints are
   * unused there).
   */
  getVaultSessionToken?: () => string;
}

export function configureApi(config: ApiRuntimeConfig): void {
  _client = config.client;
  _getAuthHeaders = config.getAuthHeaders;
  if (config.getVaultSessionToken) {
    _getVaultSessionToken = config.getVaultSessionToken;
  }
}

export function getHttpClient(): Client {
  if (!_client) {
    throw new Error(
      "API client is not configured — call configureApi() during app startup (see apps/*/src/lib/apiClient.ts)."
    );
  }
  return _client;
}

export function withAuthHeaders(
  extra?: Record<string, string>
): Promise<Record<string, string>> {
  return _getAuthHeaders(extra);
}

export function getVaultSessionToken(): string {
  return _getVaultSessionToken();
}

/**
 * Install the raw-body request interceptor on a client.
 *
 * Some endpoints (encrypted file uploads) send a raw `Blob`/binary body with no
 * body serializer, so the generated client leaves the `content-type` unset. This
 * interceptor gives such requests a sensible content-type (the blob's own type,
 * or `application/octet-stream`). Both apps need identical behaviour, so it lives
 * here rather than being copy-pasted into each app's `apiClient.ts`.
 */
export function installRawBodyInterceptor(client: Client): void {
  client.interceptors.request.use((request, options) => {
    const opts = options as { body?: unknown; serializedBody?: unknown };
    if (opts.body && !("serializedBody" in options)) {
      const headers = new Headers(request.headers);
      if (!headers.has("content-type")) {
        if (opts.body instanceof Blob && opts.body.type) {
          headers.set("content-type", opts.body.type);
        } else {
          headers.set("content-type", "application/octet-stream");
        }
      }
      return new Request(request.url, {
        method: request.method,
        headers,
        body: opts.body as BodyInit,
      });
    }
    return request;
  });
}
